import React, { useEffect, useState } from 'react';
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from 'react-router-dom';
import { allUserListApi, updateUserApi } from '../../api';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { AdminTable, ActionCell } from '../../common/adminTable';
import { useConfirm } from '../../common/confirmDialog/useConfirm';
import Paginations from '../../common/pagination/Paginations';
import configuration from '../../config/Config';
import UserImg from '../../common/assests/sampleuser.svg';

const AllUserList = () => {
  const token = useSelector((state) => state?.user?.user?.token);
  const confirm = useConfirm();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [change, setChange] = useState(false);
  const [allUserData, setAllUserData] = useState([]);

  const handleAllUserList = async () => {
    try {
      setLoading(true);
      const response = await allUserListApi({ token, currentPage, type: 1 });
      setAllUserData(response?.users || []);
      setCurrentPage(response?.currentPage);
      setTotalPages(response?.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("error", error);
    }
  };

  useEffect(() => {
    handleAllUserList();
  }, [currentPage, change]);

  const handleDeleteUser = async (id) => {
    const isConfirmed = await confirm({ title: "Delete User", description: "Are you sure you want to delete this user?", confirmText: "Delete", destructive: true });
    if (!isConfirmed) return;
    try {
      const response = await updateUserApi({ token, id, account_status: 4 });
      setChange(!change);
      toast.success(response.message);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleBlockUser = async (id) => {
    const isConfirmed = await confirm({ title: "Block User", description: "Are you sure you want to block this user?", confirmText: "Block" });
    if (!isConfirmed) return;
    try {
      const response = await updateUserApi({ token, id, account_status: 3 });
      setChange(!change);
      toast.success(response.message);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleResumeUser = async (id) => {
    const isConfirmed = await confirm({ title: "Resume User", description: "Are you sure you want to resume this user?", confirmText: "Resume" });
    if (!isConfirmed) return;
    try {
      const response = await updateUserApi({ token, id, account_status: 2 });
      setChange(!change);
      toast.success(response.message);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const columns = [
    {
      field: "user",
      headerName: "User",
      minWidth: 160,
      renderCell: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img
            src={row.picture ? `${configuration.cdnFront}/${row.picture}` : UserImg}
            alt={row.fullname || "User"}
            style={{ height: 36, width: 36, borderRadius: "50%", flexShrink: 0 }}
          />
          <Typography sx={{ fontFamily: "Heebo", fontSize: "13px", color: "#362B24" }}>
            {row.fullname || "-"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "bio",
      headerName: "Bio",
      minWidth: 140,
      cellSx: { whiteSpace: "nowrap", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" },
      renderCell: (r) => r.bio || "-",
    },
    { field: "email", headerName: "Email", minWidth: 160, renderCell: (r) => r.email || "-" },
    { field: "phone", headerName: "Mobile No", width: 130, renderCell: (r) => r.phone || "-" },
    {
      field: "address",
      headerName: "Address",
      minWidth: 160,
      cellSx: { whiteSpace: "nowrap" },
      renderCell: (r) =>
        r.address ? [r.address.address || ""].filter(Boolean).join(", ") : "-",
    },
    {
      field: "__actions",
      headerName: "",
      width: 120,
      align: "right",
      renderCell: (row) => (
        <ActionCell
          actions={[
            {
              label: "View",
              icon: <VisibilityIcon fontSize="small" />,
              color: "#065F46",
              onClick: () => navigate(`/user-management/user-information/${row._id}`, { state: { data: row } }),
            },
            row.account_status === 3
              ? {
                  label: "Resume",
                  icon: <LockOpenIcon fontSize="small" />,
                  color: "#065F46",
                  onClick: () => handleResumeUser(row._id),
                }
              : {
                  label: "Block",
                  icon: <LockIcon fontSize="small" />,
                  color: "#362B24",
                  onClick: () => handleBlockUser(row._id),
                },
            {
              label: "Delete",
              icon: <DeleteIcon fontSize="small" />,
              color: "#EA001B",
              onClick: () => handleDeleteUser(row._id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Card sx={{ padding: "20px", borderRadius: "25px", marginTop: "40px", marginBottom: "30px" }}>
      <AdminTable
        columns={columns}
        rows={allUserData}
        loading={loading}
        rowKey="_id"
        emptyText="No users found"
        rowSx={(row) => row.account_status === 3 ? { opacity: 0.65 } : {}}
      />
      <Paginations
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={(page) => { setLoading(true); setCurrentPage(page); }}
        disabled={loading}
      />
    </Card>
  );
};

export default AllUserList;
